import { StepType } from '../../app/core/enums/step-type.enum';
import { ModuleV1Read, VersionReadModel } from '../../app/core/models/module-v1-read.model';
import { getStepParameterAccordingToType, ModuleParameter, StepOutputParameter, StepParameter } from '../../app/core/models/parameter.model';
import { RunStep } from '../../app/core/models/run-step.model';
import { Step } from '../../app/core/models/step.model';
import { WorkflowNode, WorkflowSpecificationModel } from '../../app/core/models/workflow-specification';
import { Workflow } from '../../app/core/models/workflow.model';

export function getLatestAvailableModuleVersion(module: ModuleV1Read): VersionReadModel {
    const result = module.draftVersion != null && module.draftVersion.specification != null ? module.draftVersion : module.latestPublicVersion;
    if (result == null) {
        throw new Error('No version found for module ' + module.title);
    }
    return result;
}

export function determineDefineInputConnectionType(step: RunStep | Step, workflow: Workflow | null): 'file' | 'file_list' | undefined {
    let isConnectedTo: 'file' | 'file_list' | undefined = undefined;

    if (workflow != null) {
        const workflowSpec: WorkflowSpecificationModel = workflow.getSpecification();

        workflowSpec.workflow.flow.forEach((flow: WorkflowNode) => {
            if (flow.run.input != null) {
                // Check if there's either file or file_list among the input parameters of OTHER steps,
                // so we can determine if any step is connected to ours. Only one value can be connected, so we need to check for .operation_id
                for (const [, value] of Object.entries(flow.run.input)) {
                    // If 'file' is found among imput parameters and its operation_id matches our flowId, then it means that our
                    // starter step is connected to the file parameter
                    if (value != null && value.operation_id != null && value.operation_id === step.flowId) {
                        // .output holds the type ('file' or 'file_list') of the connected param
                        isConnectedTo = value.output;
                        return;
                    }
                }
            }
        });
    }

    return isConnectedTo;
}

export function addUrlsToDefineInputModule(step: Step, urls: string[], workflowDefineInputConnectedType?: 'file' | 'file_list'): Step {
    const convertRes: { param: StepParameter | undefined; isInputSingleFile: boolean } = convertDefineInputResultToStepParameter(
        urls,
        step,
        workflowDefineInputConnectedType,
    );

    if (convertRes.param != null) {
        // before adding, we need to clear the old input parameter (when it's file, we need to clear file_list i.e.)
        if (step.type !== StepType.OldWorkflowStarter) {
            if (convertRes.isInputSingleFile === true) {
                step.clearInputParameterValue('file_list');
            } else {
                step.clearInputParameterValue('file');
            }
        }
        step.setInputParameter(convertRes.param);
    }

    return step;
}

function convertDefineInputResultToStepParameter(
    input: string | Array<string>,
    editorStep: Step,
    workflowDefineInputConnectedType: 'file' | 'file_list' | undefined,
): { param: StepParameter | undefined; isInputSingleFile: boolean } {
    let isInputSingleFile: boolean = true;

    let key: string = 'file';
    let value: string | Array<string> = input;

    // Check if DefineInput is connected
    if (workflowDefineInputConnectedType != null) {
        key = workflowDefineInputConnectedType;

        if (workflowDefineInputConnectedType === 'file_list' && Array.isArray(input)) {
            value = input;
        } else if (workflowDefineInputConnectedType === 'file_list' && !Array.isArray(input)) {
            value = [input];
        }
    } else {
        // If DefineInput is not connected, we just determine the conn type according to if its an array or not.
        // determine key
        if (Array.isArray(input) && input.length > 1) {
            key = 'file_list';
            value = input;
            isInputSingleFile = false;
        }
    }

    // Fallback for old define input types:
    if (editorStep.type === StepType.OldWorkflowStarter || editorStep.type === StepType.OldSingleFileUpload) {
        key = 'src_url';
    }

    const param: StepParameter | undefined = convertKeyValuePairToStepParameter([key, value], editorStep);
    return { param, isInputSingleFile };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function convertKeyValuePairToStepParameter(keyValuePair: Array<any>, step: Step, workflow?: Workflow): StepParameter | undefined {
    const key = keyValuePair[0];
    const val = keyValuePair[1];

    let param: StepParameter | undefined;

    // Check if val is an object. typeof val returns 'object' for arrays as well, so we have to make an additional Array check
    // If operation_id is not null only then it means it is connection parameter.
    if (val != null && typeof val === 'object' && val.operation_id != null && !Array.isArray(val) && workflow != null) {
        const sourceFlowId = val.operation_id;
        const sourceOutputKey = val.output;
        const sourceStep: Step | undefined = workflow.getStep(sourceFlowId);

        if (sourceStep != null) {
            const sourceOutputParameter: ModuleParameter | undefined = sourceStep.moduleVersion.getOutputParameter(sourceOutputKey);

            if (sourceOutputParameter != null) {
                param = new StepOutputParameter(val.operation_id, sourceOutputParameter.type, sourceOutputKey, key);
            }
        }
    } else if (step != null) {
        const moduleParameter: ModuleParameter | undefined = step.moduleVersion.getInputParameter(key);
        if (moduleParameter != null) {
            param = getStepParameterAccordingToType(moduleParameter);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (param as any).value = val;
        }
    }

    return param;
}
